import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useJsonData } from '../../hooks/useJsonData';

interface Step {
  icon: string;
  number: string;
  title: string;
  description: string;
  color: string;
}

interface HowItWorksData {
  steps: Step[];
}

export function HowItWorks() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const { data, loading } = useJsonData<HowItWorksData>('/data/how-it-works.json');

  const steps = data?.steps || [];

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-14"
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">
            How It Works
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
            Structured implementation process.
          </h2>
        </motion.div>

        {/* Loading */}
        {loading && (
          <div className="space-y-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-white rounded animate-pulse" />
            ))}
          </div>
        )}

        {/* Steps — vertical timeline */}
        {!loading && (
          <div className="relative">
            {/* Connector line */}
            <div className="absolute left-5 top-6 bottom-6 w-px bg-gray-200 hidden sm:block" />

            <div className="space-y-0">
              {steps.map((step, index) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: -16 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="relative flex gap-8 sm:gap-12 pb-10 last:pb-0"
                >
                  {/* Step circle */}
                  <div className="relative z-10 shrink-0">
                    <div className="w-10 h-10 rounded-full border-2 border-p-600 bg-white/80 backdrop-blur-sm shadow-md flex items-center justify-center">
                      <span className="text-xs font-bold text-p-600">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pt-1.5 pb-2 bg-white/60 backdrop-blur-sm border border-gray-100 rounded-xl px-6 py-4 shadow-sm hover:shadow-md hover:bg-white transition-all flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed max-w-xl">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
